//-tech-debt-react-query;
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CacheKey } from "../types/cache-keys";
import {
  Customer,
  CustomerChallenge,
  CustomerCompliance,
  CustomerPriority,
  CustomerSite,
  LANTopology,
  WANTopology,
} from "../types/customer";
import {
  useAddItemMutation,
  useDeleteItemMutation,
  useFetchItems,
  useUpdateItemMutation
} from "./base-hooks";
import { deleteItem, fetchItem, fetchItems, postItem, putItem } from "../support/queries";
import { Application } from "../types/application";
import { Product, ProductAssignmentPayload } from "../types/product";
import { DEFAULT_QUERY_OPTIONS } from "../support/constants";
import { PartialWithId } from "../types/base-types";

// ---- Fetches ----
// Source and destination routes are different, so providing custom fetchers instead of using generic base
const useFetchCustomer = (id: string, params?, options?) => {
  return useQuery<Customer | null>({
    queryKey: [CacheKey.Customer, id],
    queryFn: async () => {
      let sfCustomer: Customer = null;
      try {
        sfCustomer = await fetchItem(`sf/customer/${id}`, {});
      } catch (error) {
        console.error('Error fetching DB customer:', error);
      }
      if (!sfCustomer) return null;

      return {
        ...sfCustomer,
        id: sfCustomer.Id
      } as Customer;
    },
    ...{
        enabled: !!id,
      },
  });
}

const useUpdateCustomerMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<
    Customer,
    Error,
    Partial<Customer>,
    { previous: Customer | undefined; queryKey: [string, string] }
  >({
    mutationFn: (payload) => putItem('db/customer', payload),
    onMutate: async (updated) => {
      const queryKey: [string, string] = [CacheKey.Customer, updated.id];
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<Customer>(queryKey);
      queryClient.setQueryData<Customer>(queryKey, (prev) => ({ ...prev, ...updated }));

      return { previous, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous && context?.queryKey) {
        queryClient.setQueryData(context.queryKey, context.previous);
      }
    },
    onSettled: (_response, _error, _variables, context) => {
      if (context?.queryKey) {
        queryClient.invalidateQueries({ queryKey: context.queryKey });
      }
    },
  });
};

const useFetchCustomerApplications = (
  params?: { customer: string },
  options?
) => useFetchItems<Application[]>(`db/application`, () => [CacheKey.CustomerApplication, ...(params ? [params.customer] : [])], params, options);

const useFetchCustomerChallenges = (
  params?: { customer: string },
  options?
) => useFetchItems<CustomerChallenge[]>('db/customer-challenge', () => [CacheKey.CustomerChallenge, ...(params ? [params.customer] : [])], params, options);

const useFetchCustomerCompliances = (
  params?: { customer: string },
  options?
) => useFetchItems<CustomerCompliance[]>('db/customer-compliance', () => [CacheKey.CustomerCompliance, ...(params ? [params.customer] : [])], params, options);

const useFetchCustomerProducts = (
  customerId: string,
  params?,
  options?
) => useFetchItems<Product[]>(`db/customer/${customerId}/product`, () => [CacheKey.CustomerProduct, customerId], params, options);

const useFetchCustomerProductAssignments = (
  params?,
  options?
) => useFetchItems('db/customer-owned-products', () => [CacheKey.CustomerProductAssignment, ...(params ? [params.customer] : [])], params, options);

const useFetchCustomerPriorities = (
  params?: { customer: string },
  options?
) => useFetchItems<CustomerPriority[]>('db/customer-priority', () => [CacheKey.CustomerPriority, ...(params ? [params.customer] : [])], params, options);

const useFetchCustomerSites = (
  params?: { customer: string },
  options?
) => useFetchItems<CustomerSite[]>('db/customer-site', () => [CacheKey.CustomerSite, ...(params ? [params.customer] : [])], params, options);

const useFetchLANTopologies = (
  params?: { customer: string },
  options?
) => useFetchItems<LANTopology[]>('db/lan-topology', () => [CacheKey.LanTopology, ...(params ? [params.customer] : [])], params, options);

const useFetchWANTopologies = (
  params?: { customer: string },
  options?
) => useFetchItems<WANTopology[]>('db/wan-topology', () => [CacheKey.WanTopology, ...(params ? [params.customer] : [])], params, options);

const useFetchCustomerActivityHistory = (params, options?) => useQuery({
  queryKey: [CacheKey.CustomerActivityHistory, params.customer],
  queryFn: () => fetchItems(`sf/query`, {
    q: `SELECT ActivityDate, Description, WhatId, Status, Subject, OwnerId, Priority, SE_Service_Type__c FROM Task WHERE WhatId = '${params.customer}' ORDER BY CreatedDate DESC`,
  }),
  ...{
      ...DEFAULT_QUERY_OPTIONS,
      enabled: !!params.customer,
    },
});

// ---- Mutations ----
// Application
const useAddCustomerApplicationMutation = () => useAddItemMutation<Application>(
  'db/application',
  (payload) => [CacheKey.CustomerApplication, payload.customer]
);
const useUpdateCustomerApplicationMutation = () => useUpdateItemMutation<Application>(
  'db/application',
  (payload) => [CacheKey.CustomerApplication, payload.customer, payload.id]
);
const useDeleteCustomerApplicationMutation = () => useDeleteItemMutation<PartialWithId<Application> & { customer: Customer['id'] }>(
  'db/application',
  (payload) => [CacheKey.CustomerApplication, payload.customer, payload.id]
);

// CustomerChallenge
const useAddCustomerChallengeMutation = () => useAddItemMutation<Partial<CustomerChallenge>>(
  'db/customer-challenge',
  (payload) => [CacheKey.CustomerChallenge, payload.customer]
);
const useDeleteCustomerChallengeMutation = () => useDeleteItemMutation<PartialWithId<CustomerChallenge> & { customer: Customer['id'] }>(
  'db/customer-challenge',
  (payload) => [CacheKey.CustomerChallenge, payload.customer, payload.id]
);

// CustomerCompliance
const useAddCustomerComplianceMutation = () => useAddItemMutation<Partial<CustomerCompliance>>(
  'db/customer-compliance',
  (payload) => [CacheKey.CustomerCompliance, payload.customer]
);
const useDeleteCustomerComplianceMutation = () => useDeleteItemMutation<PartialWithId<CustomerCompliance> & { customer: Customer['id'] }>(
  'db/customer-compliance',
  (payload) => [CacheKey.CustomerCompliance, payload.customer, payload.id]
);

// CustomerOwnedProducts
const useAddCustomerProductAssignmentsMutation = () => {
  return async (productPayloads: ProductAssignmentPayload[]) => {
    await Promise.all(productPayloads.map(payload => postItem('db/customer-owned-products', payload)));
  };
}

const useDeleteCustomerProductAssignmentsMutation = () => {
  return async (assignmentIds: number[]) => {
    await Promise.all(assignmentIds.map(id => deleteItem(`db/customer-owned-products/${id}`)));
  };
}

// CustomerPriority
const useAddCustomerPriorityMutation = () => useAddItemMutation<Partial<CustomerPriority>>(
  'db/customer-priority',
  (payload) => [CacheKey.CustomerPriority, payload.customer]
);
const useDeleteCustomerPriorityMutation = () => useDeleteItemMutation<PartialWithId<CustomerPriority> & { customer: Customer['id'] }>(
  'db/customer-priority',
  (payload) => [CacheKey.CustomerPriority, payload.customer, payload.id]
);

// CustomerSite
const useAddCustomerSiteMutation = () => useAddItemMutation<CustomerSite>(
  'db/customer-site',
  (payload) => [CacheKey.CustomerSite, payload.customer]
);
const useUpdateCustomerSiteMutation = () => useUpdateItemMutation<CustomerSite>(
  'db/customer-site',
  (payload) => [CacheKey.CustomerSite, payload.customer, payload.id]
);
const useDeleteCustomerSiteMutation = () => useDeleteItemMutation<PartialWithId<CustomerSite> & { customer: Customer['id'] }>(
  'db/customer-site',
  (payload) => [CacheKey.CustomerSite, payload.customer, payload.id]
);

// LANTopology
const useAddLANTopologyMutation = () => useAddItemMutation<LANTopology>(
  'db/lan-topology', (payload) => [CacheKey.LanTopology, payload.customer]);

// WANTopology
const useAddWANTopologyMutation = () => useAddItemMutation<WANTopology>(
  'db/wan-topology', (payload) => [CacheKey.WanTopology, payload.customer]
);


export {
  useFetchCustomer,
  useFetchCustomerActivityHistory,
  useFetchCustomerApplications,
  useFetchCustomerChallenges,
  useFetchCustomerCompliances,
  useFetchCustomerProductAssignments,
  useFetchCustomerProducts,
  useFetchCustomerPriorities,
  useFetchCustomerSites,
  useFetchLANTopologies,
  useFetchWANTopologies,

  useUpdateCustomerMutation,

  useAddCustomerApplicationMutation,
  useUpdateCustomerApplicationMutation,
  useDeleteCustomerApplicationMutation,

  useAddCustomerChallengeMutation,
  useDeleteCustomerChallengeMutation,

  useAddCustomerComplianceMutation,
  useDeleteCustomerComplianceMutation,

  useAddCustomerProductAssignmentsMutation,
  useDeleteCustomerProductAssignmentsMutation,

  useAddCustomerPriorityMutation,
  useDeleteCustomerPriorityMutation,

  useAddCustomerSiteMutation,
  useUpdateCustomerSiteMutation,
  useDeleteCustomerSiteMutation,

  useAddLANTopologyMutation,
  useAddWANTopologyMutation,
};